import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import AuthService from "../../../api/services/AuthService";
import { pairMatchesContractFilter, rowBetweenDateInputs, settlementMatchesContractCode, spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import SpotOrdersExportModal from "./SpotOrdersExportModal";
import OrderHistoryDownloadButtons from "./OrderHistoryDownloadButtons";
import { hideThenShowExport, showExportModal } from "./orderExportHelpers";
import { EXPORT_KIND_FUTURES_TRADES } from "./ordersHistoryExport";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";
import {
  extractFuturesTrades,
  mapFuturesTradeRow,
  toFuturesApiSymbol,
} from "./futuresProfileOrderHelpers";

const EXPORT_MODAL_ID = "exportFuturesTradeHistoryModal";
const MOB_DL_SEL = "#mob-trade-history-filters-dl";

const SETTLEMENT_OPTIONS = [
  { value: "usdt", label: "USDT" },
  { value: "usdc", label: "USDC" },
  { value: "busd", label: "BUSD" },
];

const ORDER_DESKTOP_COL_COUNT = 8;

const CONTRACT_OPTIONS = [
  { value: "all", label: "All" },
  { value: "btcusdt", label: "BTCUSDT" },
  { value: "ethusdt", label: "ETHUSDT" },
  { value: "bnbusdt", label: "BNBUSDT" },
];

const FETCH_LIMIT = 100;

function DatePickerCell({ value, onChange, ariaLabel, inputRef, placeholder = "From" }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value || placeholder}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const FuturesTradeHistory = ({ userDetails }) => {
  const initRange = spotActivityDefaultLocalRange();
  const [settlement, setSettlement] = useState("usdt");
  const [contract1, setContract1] = useState("all");
  const [contract2, setContract2] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [exportPreset, setExportPreset] = useState("custom");

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const loadTrades = useCallback(async () => {
    try {
      const res = await AuthService.futuresExecutions({
        symbol: toFuturesApiSymbol(contract1),
        from: dateFrom ? `${dateFrom}T00:00:00.000Z` : undefined,
        to: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      const userId = userDetails?._id || userDetails?.id;
      const mapped = extractFuturesTrades(res).map((r, idx) => mapFuturesTradeRow(r, idx, userId));
      setRows(mapped);
    } catch {
      setRows([]);
    }
  }, [dateFrom, dateTo, contract1, userDetails]);

  useEffect(() => {
    void loadTrades();
  }, [loadTrades]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!settlementMatchesContractCode(row.rawSymbol || row.contract, settlement)) return false;
      if (!pairMatchesContractFilter(row.rawSymbol || row.contract, contract1)) return false;
      if (contract2 !== "all" && !pairMatchesContractFilter(row.rawSymbol || row.contract, contract2)) return false;
      if (!rowBetweenDateInputs(row.executedAt, dateFrom, dateTo)) return false;
      return true;
    });
  }, [rows, settlement, contract1, contract2, dateFrom, dateTo]);

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setSettlement("usdt");
    setContract1("all");
    setContract2("all");
    setDateFrom(from);
    setDateTo(to);
  };

  const openDownload = () => {
    setExportPreset("custom");
    showExportModal(EXPORT_MODAL_ID);
  };

  const openDownloadFromMobile = () => {
    setExportPreset("custom");
    hideThenShowExport(MOB_DL_SEL, EXPORT_MODAL_ID);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
    <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter trade history">
      <div className="so-toolbar__left">
        <CustomDropdown
          id={`${pref}fth-dd-settlement`}
          label="Settlement Coin"
          value={settlement}
          options={SETTLEMENT_OPTIONS}
          onChange={setSettlement}
          menuKey={`${pref}fth-settlement`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}fth-dd-contract-1`}
          label="Contract"
          value={contract1}
          options={CONTRACT_OPTIONS}
          onChange={setContract1}
          menuKey={`${pref}fth-contract1`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <CustomDropdown
          id={`${pref}fth-dd-contract-2`}
          label="Contract"
          value={contract2}
          options={CONTRACT_OPTIONS}
          onChange={setContract2}
          menuKey={`${pref}fth-contract2`}
          openKey={openDropdown}
          onOpenToggle={setOpenDropdown}
        />
        <div className="so-dateBlock">
          <span className="so-dateBlock__label">Date</span>
          <div className="so-dateRow">
            <DatePickerCell value={dateFrom} onChange={setDateFrom} placeholder="From" ariaLabel="Start date" inputRef={fromRef} />
            <span className="so-dateArrow" aria-hidden="true">
              →
            </span>
            <DatePickerCell value={dateTo} onChange={setDateTo} placeholder="To" ariaLabel="End date" inputRef={toRef} />
            <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="so-toolbar__right">
        {!stacked ? (
          <OrderHistoryDownloadButtons onDownload={openDownload} />
        ) : null}
      </div>
    </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-trade-history-title">
            <h1 id="so-trade-history-title" className="so-page__title">
              Trade History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
                { label: "Order History", to: "/user_profile/orders/futures_order_history" },
                { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
                { label: "Position History", to: "/user_profile/orders/position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap">
              <table className="so-table so-table--rowLines so-table--tradeHistory">
                <thead>
                  <tr>
                    <th scope="col">Contract</th>
                    <th scope="col">Executed At</th>
                    <th scope="col">Side</th>
                    <th scope="col">Size</th>
                    <th scope="col">Fill Price</th>
                    <th scope="col">Fee</th>
                    <th scope="col">Realized PnL</th>
                    <th scope="col">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.contract}</td>
                      <td className="so-num">{row.executedAtDisplay}</td>
                      <td>
                        <span className={row.side === "Buy" ? "so-side--buy" : row.side === "Sell" ? "so-side--sell" : undefined}>
                          {row.side}
                        </span>
                      </td>
                      <td className="so-num">
                        <span className="so-fillQty--pos">{row.size}</span>
                      </td>
                      <td className="so-num">{row.fillPrice}</td>
                      <td className="so-num">{row.fee}</td>
                      <td className={`so-num ${row.pnlNum > 0 ? "so-side--buy" : row.pnlNum < 0 ? "so-side--sell" : ""}`}>
                        {row.pnl}
                      </td>
                      <td>{row.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Trade history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Trade History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/futures_open_orders" },
              { label: "Order History", to: "/user_profile/orders/futures_order_history" },
              { label: "Trade History", to: "/user_profile/orders/futures_trade_history" },
              { label: "Position History", to: "/user_profile/orders/position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-trade-history-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
              downloads={
                <OrderHistoryDownloadButtons stacked onDownload={openDownloadFromMobile} />
              }
            />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((row) => (
            <OrderMobileCardShell
              key={`m-${row.id}`}
              desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
              popupTitle="Trade history details"
              heroPrimary={row.contract}
              heroDatetime={row.executedAtDisplay}
            >
              <div className="so-mobileCard__row">
                <span>Executed At</span>
                <span className="so-num">{row.executedAtDisplay}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Side</span>
                <span className={row.side === "Buy" ? "so-side--buy" : row.side === "Sell" ? "so-side--sell" : undefined}>
                  {row.side}
                </span>
              </div>
              <div className="so-mobileCard__row">
                <span>Size</span>
                <span className="so-num so-fillQty--pos">{row.size}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Fill Price</span>
                <span className="so-num">{row.fillPrice}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Fee</span>
                <span className="so-num">{row.fee}</span>
              </div>
              <div className="so-mobileCard__row">
                <span>Realized PnL</span>
                <span className={`so-num ${row.pnlNum > 0 ? "so-side--buy" : row.pnlNum < 0 ? "so-side--sell" : ""}`}>
                  {row.pnl}
                </span>
              </div>
              <div className="so-mobileCard__row">
                <span>Role</span>
                <span>{row.role}</span>
              </div>
            </OrderMobileCardShell>
          ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Trade history pages"
          />
        </div>
      </div>
      <SpotOrdersExportModal
        modalId={EXPORT_MODAL_ID}
        exportKind={EXPORT_KIND_FUTURES_TRADES}
        defaultFrom={dateFrom}
        defaultTo={dateTo}
        defaultPreset={exportPreset}
      />
    </div>
  );
};

export default FuturesTradeHistory;

